import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Hash, TrendingUp, Clock, MapPin, RefreshCw, Loader2 } from "lucide-react";
import { formatGeneratedAt } from "../lib/datetime";

function StatBar({ label, value, color = "bg-bm-cyan" }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] font-mono mb-0.5">
        <span className="text-bm-dim uppercase tracking-wider">{label}</span>
        <span className="text-white">{value}</span>
      </div>
      <div className="h-0.5 bg-white/5">
        <div className={`h-full ${color}`} style={{ width: `${Math.min(100, value)}%` }}></div>
      </div>
    </div>
  );
}

function ChipRow({ label, items, accent }) {
  if (!items?.length) return null;
  return (
    <div>
      <div className="label-mono text-bm-dim mb-1">{label}</div>
      <div className="flex flex-wrap gap-1">
        {items.map((x) => (
          <span key={x} className={`text-[11px] font-mono px-1.5 py-0.5 border ${accent}`}>{x}</span>
        ))}
      </div>
    </div>
  );
}

function CulturalCard({ c, idx }) {
  return (
    <div
      data-testid={`cultural-card-${c.id}`}
      className="bm-card fade-up p-5 flex flex-col gap-4"
      style={{ animationDelay: `${Math.min(idx * 50, 500)}ms` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Hash className="w-5 h-5 text-bm-amber" />
          <div>
            <div className="font-heading text-2xl font-black tracking-tight text-white" data-testid={`cultural-tag-${c.id}`}>{c.hashtag}</div>
            <div className="label-mono text-bm-dim">{c.platform} · {c.geography}</div>
          </div>
        </div>
        <div className="flex items-center gap-1 font-mono text-sm text-bm-green">
          <TrendingUp className="w-3.5 h-3.5" />
          <span>+{c.velocity_7d_pct}% / 7d</span>
        </div>
      </div>

      <p className="text-sm text-bm-text/90 leading-relaxed">{c.cluster_theme}</p>

      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/5">
        <StatBar label="VOLUME (POSTS/W)" value={Math.min(100, Math.round(c.volume / 50000))} color="bg-bm-cyan" />
        <StatBar label="CONFIDENCE" value={c.confidence} color="bg-bm-amber" />
      </div>

      <ChipRow label="RECURRING AESTHETICS" items={c.recurring_aesthetics} accent="border-bm-cyan/30 text-bm-cyan" />
      <ChipRow label="RECURRING INGREDIENTS" items={c.recurring_ingredients} accent="border-bm-amber/30 text-bm-amber" />
      <ChipRow label="RECURRING RITUALS" items={c.recurring_rituals} accent="border-bm-green/30 text-bm-green" />
      <ChipRow label="ARCHETYPES" items={c.recurring_archetypes} accent="border-white/15 text-bm-muted" />

      {c.sample_content_themes?.length > 0 && (
        <div className="border-t border-white/5 pt-3">
          <div className="label-mono text-bm-dim mb-1">SAMPLE CONTENT THEMES</div>
          <ul className="space-y-1 text-xs text-bm-muted">
            {c.sample_content_themes.map((t) => (
              <li key={t} className="flex gap-2"><span className="text-bm-amber shrink-0">→</span>{t}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center justify-between text-[10px] font-mono pt-2 border-t border-white/5">
        <div className="flex items-center gap-1 text-bm-dim">
          <MapPin className="w-3 h-3" />{c.geography}
        </div>
        <div className="flex items-center gap-1 text-bm-dim" data-testid={`cultural-generated-at-${c.id}`}>
          <Clock className="w-3 h-3" />Generated {formatGeneratedAt(c.generated_at || c.created_at)}
        </div>
      </div>
    </div>
  );
}

export default function CulturalSignals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = async () => {
    const d = await api.culturalSignals();
    setItems(d.items || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // Fire-once on mount — api / setters are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshLive = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const r = await api.refreshCulturalSignals();
      setLastRefresh(r);
      await load();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="border-b border-white/10 pb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-mono text-bm-amber">CULTURAL VELOCITY</div>
          <h1 className="font-heading text-4xl font-black tracking-tight mt-1" data-testid="page-title">
            The vibe beneath the products.
          </h1>
          <p className="text-sm text-bm-muted mt-2 max-w-3xl">
            Hashtag-driven cultural-velocity clusters — not products. Track aesthetics, ingredients, rituals, and archetypes converging on the conversation. Distinct from Product or Competitor signals.
          </p>
        </div>
        <button
          onClick={refreshLive}
          disabled={refreshing}
          data-testid="refresh-cultural-btn"
          className="flex items-center gap-2 px-3 h-9 text-xs font-mono bg-bm-amber text-black hover:bg-white transition-colors disabled:opacity-50"
        >
          {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          REFRESH LIVE COUNTERS
        </button>
      </div>

      {lastRefresh && (
        <div className="bm-card p-3 text-xs font-mono flex items-start gap-2 border-l-2 border-bm-green" data-testid="refresh-result">
          <div className="text-bm-green">✓</div>
          <div className="flex-1 text-bm-muted">
            Refreshed {lastRefresh.runs?.length || 0} hashtag(s) ·
            {" "}
            {lastRefresh.runs?.map((r) => `${r.hashtag} (${r.via}, vel +${r.velocity_7d_pct}%)`).join(" · ")}
          </div>
        </div>
      )}

      {loading && <div className="font-mono text-xs text-bm-cyan scanline p-6">LOADING CULTURAL CLUSTERS...</div>}

      {!loading && items.length === 0 && (
        <div className="text-center py-12 font-mono text-sm text-bm-muted">No cultural signals tracked yet.</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="cultural-grid">
        {items.map((c, i) => <CulturalCard key={c.id} c={c} idx={i} />)}
      </div>
    </div>
  );
}
